// src/pages/ProspecPage.jsx - Com modal de visualização para todos os perfis
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/common/index.css';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Navigation from '../components/common/Navigation';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import storageService from '../services/storageService'; 
import { formatarPrimeiraMaiuscula } from '../utils/formatters';
import GerarTermoButton from '../components/GerarTermoButton';
import ModalFiltrosExportacao from '../components/ModalFiltrosExportacao';
import exportExcelService from '../services/exportExcelService';
import './ProspecPage.css';
import '../components/common/CommonModal.css';
import './ProspecModalDark.css';
import {
  FileText,
  Clock,
  CheckCircle,
  TrendingUp,
  Edit,
  Eye,
  Mail,
  MessageCircle,
  Trash2,
  X,
  Download,
  Link,
  BarChart3,
  Plus,
  DollarSign,
  Search
} from 'lucide-react';

const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL?.replace(/\/api$/, '');
};

const getApiUrl = () => {
  return process.env.REACT_APP_API_URL;
};

const construirUrlDocumento = (nomeArquivo) => {
  if (!nomeArquivo) return '';
  
  // Construir URL base removendo "/api" apenas do final
  const apiUrl = process.env.REACT_APP_API_URL;
  const baseUrl = apiUrl.replace(/\/api$/, ''); // Remove "/api" apenas se estiver no final
  
  return `${baseUrl}/storage/propostas/documentos/${nomeArquivo}`;
};

// Função para visualizar documento
const visualizarDocumento = (nomeArquivo) => {
  if (!nomeArquivo) {
    console.warn('Nome do arquivo não fornecido para visualização');
    return;
  }
  
  const url = construirUrlDocumento(nomeArquivo);
  window.open(url, '_blank');
  console.log('Documento aberto em nova aba:', url);
};

// Componente simplificado - APENAS botão de visualizar
const BotoesDocumento = ({ nomeArquivo, tipoArquivo }) => {
  if (!nomeArquivo || typeof nomeArquivo !== 'string') {
    return null;
  }

  return (
    <div className="arquivo-acoes">
      <button
        type="button"
        className="btn-visualizar-doc"
        onClick={() => visualizarDocumento(nomeArquivo)}
        title={`Visualizar ${tipoArquivo}`}
      >
        <Eye size={14} />
      </button>
    </div>
  );
};

// Lista de estados brasileiros
const ESTADOS_BRASIL = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' }
];

const ProspecPage = () => {
  const navigate = useNavigate();
  const { user, getMyTeam, getConsultorName } = useAuth();
  const { showNotification } = useNotification();
  const { 
    propostas, 
    loadPropostas, 
    afterDeleteProposta,
    afterUpdateProposta,
    loadControle // ← ADICIONAR esta importação
  } = useData();
  
  const [loading, setLoading] = useState(false); 
  const [modalExportacao, setModalExportacao] = useState(false);
  const [modalEdicao, setModalEdicao] = useState({ show: false, item: null, index: -1 });
  const [modalVisualizacao, setModalVisualizacao] = useState({ show: false, item: null });
  const [consultoresDisponiveis, setConsultoresDisponiveis] = useState([]);
  const consultoresUnicos = [...new Set((propostas.data || []).map(item => item.consultor).filter(Boolean))];
  
  const [filtros, setFiltros] = useState({
    consultor: '',
    status: '',
    busca: ''
  });

  const carregarConsultores = useCallback(async () => {
    try {
      const team = getMyTeam();

      if (user?.role === 'admin' || user?.role === 'analista') {
        // ✅ Apenas consultores, gerentes e vendedores (não incluir admin/analista)
        const consultores = team.filter(member =>
          ['consultor', 'gerente', 'vendedor'].includes(member.role)
        );

        const listaFinal = [
          ...consultores.map(member => ({
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role
          })),
          { id: null, name: 'Sem consultor (AUPUS direto)', email: '', role: '' }
        ];

        setConsultoresDisponiveis(listaFinal);
      } else if (user?.role === 'consultor') {
        const funcionarios = team.filter(member =>
          member.role === 'gerente' || member.role === 'vendedor'
        );
        const listaFinal = [
          { id: user.id, name: user.name, email: user.email, role: user.role },
          ...funcionarios.map(member => ({
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role
          }))
        ];
        setConsultoresDisponiveis(listaFinal);
      } else if (user?.role === 'gerente') {
        const vendedores = team.filter(member =>
          member.role === 'vendedor'
        );
        const listaFinal = [
          { id: user.id, name: user.name, email: user.email, role: user.role },
          ...vendedores.map(member => ({
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role
          }))
        ];
        setConsultoresDisponiveis(listaFinal);
      } else if (user?.role === 'vendedor') {
        const listaFinal = [{ id: user.id, name: user.name, email: user.email, role: user.role }];
        setConsultoresDisponiveis(listaFinal);
      }
    } catch (error) {
      console.error('Erro ao carregar consultores:', error);
      setConsultoresDisponiveis([{ id: user?.id, name: user?.name || 'Erro' }]);
    }
  }, [user, getMyTeam]);

    useEffect(() => {
      carregarConsultores();
    }, [carregarConsultores]);

  const dadosFiltrados = useMemo(() => {
    let dados = propostas.data || [];

    if (filtros.consultor) {
      dados = dados.filter(item =>
        item.consultor?.toLowerCase().includes(filtros.consultor.toLowerCase())
      );
    }

    if (filtros.status) {
      dados = dados.filter(item => item.status === filtros.status);
    }

    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      dados = dados.filter(item =>
        (item.nomeCliente?.toString().toLowerCase() || '').includes(busca) ||
        (item.numeroProposta?.toString().toLowerCase() || '').includes(busca) ||
        (item.numeroUC?.toString().toLowerCase() || '').includes(busca) ||
        (item.apelido?.toString().toLowerCase() || '').includes(busca)
      );
    }

    return dados;
  }, [propostas.data, filtros]);

  const limparFiltros = () => {
    setFiltros({
      consultor: '',
      status: '',
      busca: ''
    });
  };

  const editarItem = (index) => {
    const item = dadosFiltrados[index];
    if (!item) return;
    
    // ✅ DEBUG: Ver como os dados chegam
    console.log('🔍 Item completo:', item);
    console.log('🔍 Documentação no item:', item.documentacao);
    console.log('🔍 Número UC:', item.numeroUC || item.numero_unidade);
    
    // ✅ BUSCAR DOCUMENTAÇÃO ESPECÍFICA DA UC
    const numeroUC = item.numeroUC || item.numero_unidade;
    let documentacaoUC = {};

    // Se tem proposta expandida com documentação
    if (item.documentacao && typeof item.documentacao === 'object') {
      console.log('🔍 DEBUG contratoLocacao - documentacao completa:', item.documentacao);
      console.log('🔍 DEBUG contratoLocacao - numeroUC:', numeroUC);
      console.log('🔍 DEBUG contratoLocacao - documentacao[numeroUC]:', item.documentacao[numeroUC]);
      documentacaoUC = item.documentacao[numeroUC] || {};
      console.log('🔍 Documentação da UC encontrada:', documentacaoUC);
      console.log('🔍 DEBUG contratoLocacao no documentacaoUC:', documentacaoUC.contratoLocacao);
    } else {
      console.log('🔍 Documentação não encontrada ou não é objeto');
    }
    
    // Adicionar documentação específica da UC ao item
    const itemComDocumentacao = {
      ...item,
      // Dados da documentação específica da UC
      tipoDocumento: documentacaoUC.tipoDocumento || '',
      documentacao: item.documentacao,
      nomeRepresentante: documentacaoUC.nomeRepresentante || '',
      cpf: documentacaoUC.cpf || '',
      documentoPessoal: documentacaoUC.documentoPessoal || null,
      razaoSocial: documentacaoUC.razaoSocial || '',
      cnpj: documentacaoUC.cnpj || '',
      contratoSocial: documentacaoUC.contratoSocial || null,
      documentoPessoalRepresentante: documentacaoUC.documentoPessoalRepresentante || null,
      enderecoUC: documentacaoUC.enderecoUC || '',
      CEP_UC: documentacaoUC.CEP_UC || '',
      Bairro_UC: documentacaoUC.Bairro_UC || '',
      Cidade_UC: documentacaoUC.Cidade_UC || '',
      Estado_UC: documentacaoUC.Estado_UC || '',
      isArrendamento: documentacaoUC.isArrendamento || false,
      contratoLocacao: documentacaoUC.contratoLocacao || null,
      enderecoRepresentante: documentacaoUC.enderecoRepresentante || '',
      logradouroUC: documentacaoUC.logradouroUC || documentacaoUC.logradouro_uc || '',
      termoAdesao: documentacaoUC.termoAdesao || null,
      whatsappRepresentante: documentacaoUC.whatsappRepresentante || '',
      emailRepresentante: documentacaoUC.emailRepresentante || '',
      documentosExtras: documentacaoUC.documentosExtras || []
    };
    
    console.log('🔍 Item final com documentação:', itemComDocumentacao);
    
    setModalEdicao({ show: true, item: itemComDocumentacao, index });
  };

  const visualizarItem = (index) => {
    const item = dadosFiltrados[index];
    if (!item) return;
    setModalVisualizacao({ show: true, item });
  };

  // CORREÇÃO: Remover as linhas problemáticas da função salvarEdicao
  // Substituir a função salvarEdicao existente por esta versão corrigida:

  const salvarEdicao = async (dadosAtualizados) => {
    console.log('🟢 INÍCIO salvarEdicao - função chamada');
    console.log('📊 Dados recebidos em salvarEdicao:', dadosAtualizados);

    setLoading(true);
    console.log('🔄 Loading definido como true em salvarEdicao');

    try {
      const { item } = modalEdicao;
      const propostaId = item.propostaId || item.id?.split('-')[0];
      
      if (!propostaId) {
        showNotification('ID da proposta não encontrado para edição', 'error');
        return;
      }

      // ✅ UPLOAD DE ARQUIVOS (mantém como está)
      const camposArquivo = [
        'documentoPessoal', 'contratoSocial', 'documentoPessoalRepresentante',
        'contratoLocacao', 'termoAdesao'
      ];

      const documentacaoFinal = { ...dadosAtualizados };

      for (const campo of camposArquivo) {
        if (dadosAtualizados[campo] && dadosAtualizados[campo] instanceof File) {
          try {
            const formData = new FormData();
            formData.append('arquivo', dadosAtualizados[campo]);
            formData.append('numeroUC', item.numeroUC || item.numero_unidade);
            formData.append('tipoDocumento', campo);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/propostas/${propostaId}/upload-documento`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('aupus_token')}`
              },
              body: formData
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            documentacaoFinal[campo] = result.nomeArquivo;
            showNotification(`${campo} enviado com sucesso!`, 'success');
          } catch (error) {
            showNotification(`Erro ao enviar ${campo}: ${error.message}`, 'error');
            return;
          }
        }
      }

      // ✅ UPLOAD DE DOCUMENTOS EXTRAS
      if (dadosAtualizados.documentosExtras && dadosAtualizados.documentosExtras.length > 0) {
        const documentosExtrasFinais = [];

        for (let i = 0; i < dadosAtualizados.documentosExtras.length; i++) {
          const docExtra = dadosAtualizados.documentosExtras[i];

          if (docExtra instanceof File) {
            try {
              const formData = new FormData();
              formData.append('arquivo', docExtra);
              formData.append('numeroUC', item.numeroUC || item.numero_unidade);
              formData.append('tipoDocumento', `documentoExtra_${i}`);

              const response = await fetch(`${process.env.REACT_APP_API_URL}/propostas/${propostaId}/upload-documento`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('aupus_token')}`
                },
                body: formData
              });

              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }

              const result = await response.json();
              documentosExtrasFinais[i] = result.nomeArquivo;
              showNotification(`Documento extra ${i + 1} enviado com sucesso!`, 'success');
            } catch (error) {
              showNotification(`Erro ao enviar documento extra ${i + 1}: ${error.message}`, 'error');
              return;
            }
          } else if (typeof docExtra === 'string') {
            // Manter documento extra já existente
            documentosExtrasFinais[i] = docExtra;
          }
        }

        documentacaoFinal.documentosExtras = documentosExtrasFinais;
      }

      // ✅ DADOS LIMPOS - SEM DUPLICAÇÕES
      const dadosLimpos = {
        // Dados básicos
        nomeCliente: dadosAtualizados.nomeCliente,
        apelido: dadosAtualizados.apelido,
        ligacao: dadosAtualizados.ligacao,
        media: dadosAtualizados.media,
        distribuidora: dadosAtualizados.distribuidora,
        status: dadosAtualizados.status,
        numeroProposta: dadosAtualizados.numeroProposta,
        data: dadosAtualizados.data,
        observacoes: dadosAtualizados.observacoes || item.observacoes,
        
        // ✅ CONSULTOR E RECORRÊNCIA - APENAS UMA VEZ
        consultor_id: dadosAtualizados.consultor_id || null,
        consultor: dadosAtualizados.consultor || '',
        recorrencia: dadosAtualizados.recorrencia || (dadosAtualizados.consultor_id ? 1 : 0),
        
        // Descontos
        descontoTarifa: dadosAtualizados.descontoTarifa,
        descontoBandeira: dadosAtualizados.descontoBandeira,
        
        // Contatos
        whatsappRepresentante: dadosAtualizados.whatsappRepresentante,
        emailRepresentante: dadosAtualizados.emailRepresentante,
        logradouroUC: dadosAtualizados.logradouroUC,
        
        // IDs
        propostaId: propostaId,
        numeroUC: item.numeroUC || item.numero_unidade,
        
        // Documentação
        documentacao: {
          tipoDocumento: documentacaoFinal.tipoDocumento,
          nomeRepresentante: documentacaoFinal.nomeRepresentante,
          cpf: documentacaoFinal.cpf,
          documentoPessoal: documentacaoFinal.documentoPessoal,
          razaoSocial: documentacaoFinal.razaoSocial,
          cnpj: documentacaoFinal.cnpj,
          contratoSocial: documentacaoFinal.contratoSocial,
          logradouroUC: documentacaoFinal.logradouroUC,
          documentoPessoalRepresentante: documentacaoFinal.documentoPessoalRepresentante,
          enderecoUC: documentacaoFinal.enderecoUC,
          CEP_UC: documentacaoFinal.CEP_UC,
          Bairro_UC: documentacaoFinal.Bairro_UC,
          Cidade_UC: documentacaoFinal.Cidade_UC,
          Estado_UC: documentacaoFinal.Estado_UC,
          isArrendamento: documentacaoFinal.isArrendamento,
          contratoLocacao: documentacaoFinal.contratoLocacao,
          enderecoRepresentante: documentacaoFinal.enderecoRepresentante,
          termoAdesao: documentacaoFinal.termoAdesao,
          whatsappRepresentante: documentacaoFinal.whatsappRepresentante,
          emailRepresentante: documentacaoFinal.emailRepresentante,
          documentosExtras: documentacaoFinal.documentosExtras || []
        },
        
        // Benefícios
        beneficios: item.beneficios || []
      };

      await storageService.atualizarProspec(propostaId, dadosLimpos);
      
      // ✅ REFRESH DOS DADOS
      console.log('🔄 Atualizando dados automaticamente após salvamento...');
      await loadPropostas(1, propostas.filters, true);
      await loadControle(1, {}, true);

      setModalEdicao({ show: false, item: null, index: -1 });
      showNotification('Proposta atualizada com sucesso!', 'success');
      console.log('✅ salvarEdicao concluída com sucesso');

    } catch (error) {
      console.error('❌ Erro ao salvar edição:', error);
      console.error('❌ Stack trace:', error.stack);
      showNotification('Erro ao salvar: ' + error.message, 'error');
    } finally {
      console.log('🟢 FIM salvarEdicao - setLoading(false)');
      setLoading(false);
    }
  };
    
  // Formatar UCs a partir do item da tabela
  const formatarUCsDoItem = (item) => {
    if (item.unidades_consumidoras) {
      try {
        const ucs = JSON.parse(item.unidades_consumidoras);
        return ucs.map(uc => ({
          apelido: uc.apelido || uc.numero_unidade || 'UC',
          numeroUC: uc.numero_unidade || uc.numeroUC || '',
          ligacao: uc.ligacao || uc.tipo_ligacao || 'Monofásica',
          consumo: uc.consumo_medio || uc.media || 0,
          distribuidora: uc.distribuidora || item.distribuidora || 'N/I' // Adicionar distribuidora
        }));
      } catch (e) {
        console.warn('Erro ao parsear UCs:', e);
      }
    }
    
    return [{
      apelido: item.apelido || item.numeroUC || 'UC',
      numeroUC: item.numeroUC || item.numero_unidade || '',
      ligacao: item.ligacao || item.tipo_ligacao || 'Monofásica',
      consumo: item.media || item.consumo_medio || item.consumo || 0,
      distribuidora: item.distribuidora || 'N/I' // Adicionar distribuidora do item
    }];
  };

  // Formatar benefícios a partir do item da tabela    
  const formatarBeneficiosDoItem = (item) => {
    // Se já é um array direto (caso atual)
    if (Array.isArray(item.beneficios)) {
      return item.beneficios.map((texto, index) => ({
        numero: index + 1,
        texto: texto
      }));
    }
    
    // Se é string JSON (caso antigo)  
    if (item.beneficios && typeof item.beneficios === 'string') {
      try {
        const beneficios = JSON.parse(item.beneficios);
        
        // Se é array de strings
        if (Array.isArray(beneficios) && typeof beneficios[0] === 'string') {
          return beneficios.map((texto, index) => ({
            numero: index + 1,
            texto: texto
          }));
        }
        
        // Se é array de objetos
        if (Array.isArray(beneficios) && typeof beneficios[0] === 'object') {
          return beneficios.map((beneficio, index) => ({
            numero: beneficio.numero || (index + 1),
            texto: beneficio.texto || beneficio.toString()
          }));
        }
        
      } catch (e) {
        console.warn('Erro ao parsear benefícios:', e);
      }
    }
    
    // Retornar array vazio se não conseguir processar
    return [];
  };

  const extrairValorDesconto = (desconto) => {
    if (typeof desconto === 'string' && desconto.includes('%')) {
      return parseFloat(desconto.replace('%', ''));
    }
    return parseFloat(desconto) || 20;
  };

  const gerarPDFProposta = async (item) => {
    try {
      console.log('📄 Gerando PDF da proposta...', item);

      // ✅ VERIFICAR SE É UMA UC ESPECÍFICA OU PROPOSTA COMPLETA
      let ucEspecifica = null;
      let propostaId = null;

      // Primeira prioridade: propostaId direto
      if (item.propostaId) {
        propostaId = item.propostaId;
      }
      // Segunda prioridade: se tem ID composto, extrair proposta e UC específica
      else if (item.id && typeof item.id === 'string' && item.id.includes('-UC-')) {
        const parts = item.id.split('-UC-');
        if (parts.length >= 2 && !isNaN(Number(parts[0]))) {
          propostaId = Number(parts[0]);
          // Identificar UC específica baseada no item clicado
          ucEspecifica = {
            apelido: item.apelido || item.numero_unidade || 'UC',
            numeroUC: item.numeroUC || item.numero_unidade || '',
            numero_unidade: item.numeroUC || item.numero_unidade || '',
            ligacao: item.ligacao || item.tipo_ligacao || 'Monofásica',
            consumo: parseInt(item.media) || 0,
            consumo_medio: parseInt(item.media) || 0,
            distribuidora: item.distribuidora || ''
          };
          console.log('🎯 UC específica identificada:', ucEspecifica);
        }
      }
      // Terceira prioridade: ID direto se for numérico
      else if (item.id && !isNaN(Number(item.id))) {
        propostaId = Number(item.id);
      }

      if (!propostaId) {
        console.error('❌ ERRO CRÍTICO: ID da proposta não encontrado!', item);
        showNotification('❌ ID da proposta não encontrado - impossível gerar PDF', 'error');
        return;
      }

      // ✅ BUSCAR PROPOSTA COMPLETA COM TODAS AS UCs
      let propostaCompleta;
      try {
        console.log('🔍 Buscando proposta completa por ID:', propostaId);
        propostaCompleta = await storageService.buscarPropostaPorId(propostaId);

        if (!propostaCompleta) {
          // ✅ CORREÇÃO PARA NÃO-ADMINS: Buscar apenas no cache expandido (sem riscos de dados incorretos)
          console.warn('⚠️ Proposta não encontrada via API (comum para não-admins), buscando em cache expandido...');

          // ✅ BUSCA RIGOROSA: Filtrar do dadosFiltrados (dados já na tabela)
          // que já foram validados e expandidos corretamente
          const linhasDaMesmaProposta = dadosFiltrados.filter(linha => {
            const linhaPropostaId = linha.propostaId || linha.id?.split('-')[0];
            return Number(linhaPropostaId) === Number(propostaId);
          });

          if (linhasDaMesmaProposta.length > 0) {
            // ✅ RECONSTRUIR proposta a partir das linhas da tabela (dados confiáveis)
            const primeiraLinha = linhasDaMesmaProposta[0];

            // Validação adicional: verificar se cliente confere
            if (item.nomeCliente && primeiraLinha.nomeCliente !== item.nomeCliente) {
              console.error('❌ ERRO CRÍTICO: Cliente não confere mesmo no cache!', {
                esperado: item.nomeCliente,
                encontrado: primeiraLinha.nomeCliente
              });
              showNotification('❌ Erro de validação: Dados da proposta não conferem', 'error');
              return;
            }

            // ✅ CONSTRUIR dados da proposta a partir das linhas válidas
            propostaCompleta = {
              id: primeiraLinha.propostaId,
              numeroProposta: primeiraLinha.numeroProposta,
              nomeCliente: primeiraLinha.nomeCliente,
              consultor: primeiraLinha.consultor,
              data: primeiraLinha.data,
              descontoTarifa: primeiraLinha.descontoTarifa,
              descontoBandeira: primeiraLinha.descontoBandeira,
              inflacao: primeiraLinha.inflacao,
              tarifaTributos: primeiraLinha.tarifaTributos,
              beneficios: primeiraLinha.beneficios || [],
              // ✅ CONSTRUIR UCs a partir das linhas da tabela
              unidades_consumidoras: linhasDaMesmaProposta.map(linha => ({
                apelido: linha.apelido || linha.numero_unidade || 'UC',
                numero_unidade: linha.numeroUC || linha.numero_unidade || '',
                numeroUC: linha.numeroUC || linha.numero_unidade || '',
                ligacao: linha.ligacao || linha.tipo_ligacao || 'Monofásica',
                consumo_medio: parseInt(linha.media) || 0,
                distribuidora: linha.distribuidora || ''
              }))
            };

            console.log('✅ Proposta reconstruída a partir das linhas da tabela:', {
              id: propostaCompleta.id,
              numero: propostaCompleta.numeroProposta,
              cliente: propostaCompleta.nomeCliente,
              ucs_count: propostaCompleta.unidades_consumidoras.length
            });
          }
        }
      } catch (error) {
        console.warn('⚠️ Erro ao buscar proposta completa:', error);
        propostaCompleta = null;
      }

      // ✅ DECIDIR QUAIS UCs INCLUIR NO PDF
      let ucsParaPDF = [];

      if (ucEspecifica) {
        // Se foi clicado em uma UC específica, incluir apenas ela
        ucsParaPDF = [ucEspecifica];
        console.log('🎯 PDF será gerado apenas para a UC específica:', ucEspecifica.apelido);

        // Confirmar com o usuário se deseja incluir apenas uma UC
        const confirmar = window.confirm(`Você clicou em uma UC específica (${ucEspecifica.apelido}).
Deseja gerar o PDF apenas para esta UC?
• SIM = PDF só desta UC
• NÃO = PDF com todas as UCs da proposta`);

        if (!confirmar) {
          // Usuário escolheu incluir todas as UCs
          ucEspecifica = null;
        }
      }

      if (!ucEspecifica) {
        // Incluir todas as UCs da proposta
        if (propostaCompleta && propostaCompleta.unidades_consumidoras) {
          const ucsOriginais = Array.isArray(propostaCompleta.unidades_consumidoras)
            ? propostaCompleta.unidades_consumidoras
            : [];

          ucsParaPDF = ucsOriginais.map(uc => ({
            apelido: uc.apelido || uc.numero_unidade || 'UC',
            numeroUC: uc.numero_unidade || uc.numeroUC || '',
            numero_unidade: uc.numero_unidade || uc.numeroUC || '',
            ligacao: uc.ligacao || uc.tipo_ligacao || 'Monofásica',
            consumo: parseInt(uc.consumo_medio || uc.consumo || uc.media || 0) || 0,
            consumo_medio: parseInt(uc.consumo_medio || uc.consumo || uc.media || 0) || 0,
            distribuidora: uc.distribuidora || ''
          }));

          console.log('✅ PDF incluirá todas as UCs da proposta (dados API):', ucsParaPDF.length);
        } else {
          // ✅ CORREÇÃO FINAL: Usar sempre os dados da API quando disponível
          console.warn('⚠️ Dados da API não disponíveis, usando dados do item clicado');

          // SEMPRE usar apenas os dados do item que foi clicado
          ucsParaPDF = [{
            apelido: item.apelido || item.numero_unidade || 'UC',
            numeroUC: item.numeroUC || item.numero_unidade || '',
            numero_unidade: item.numeroUC || item.numero_unidade || '',
            ligacao: item.ligacao || item.tipo_ligacao || 'Monofásica',
            consumo: parseInt(item.media) || 0,
            consumo_medio: parseInt(item.media) || 0,
            distribuidora: item.distribuidora || ''
          }];
          console.log('✅ PDF incluirá apenas a UC clicada (dados limpos):', ucsParaPDF[0]);
        }
      }

      // ✅ PREPARAR DADOS COMPLETOS PARA O PDF COM DESCONTOS CORRETOS
      const dadosPDF = {
        numeroProposta: item.numeroProposta,
        nomeCliente: item.nomeCliente,
        consultor: item.consultor,
        data: item.data,
        // ✅ CORRIGIR DESCONTOS: Converter de % ou decimal para decimal
        descontoTarifa: parseFloat(item.descontoTarifa) / 100 || 0.2,  // 20 → 0.2
        descontoBandeira: parseFloat(item.descontoBandeira) / 100 || 0.2, // 20 → 0.2
        // ✅ USAR VALORES SALVOS NO BANCO DE DADOS DA PROPOSTA
        inflacao: (parseFloat(item.inflacao) || 2) / 100,  // Do banco: proposta.inflacao / 100
        tarifaTributos: parseFloat(item.tarifaTributos) || 1.17, // Do banco: proposta.tarifa_tributos
        observacoes: item.observacoes || '',
        ucs: ucsParaPDF, // ← CORRIGIDO: usar UCs selecionadas (específica ou todas)
        beneficios: []
      };

      // ✅ PROCESSAR BENEFÍCIOS CORRETAMENTE
      if (item.beneficios && typeof item.beneficios === 'string') {
        try {
          const beneficiosArray = JSON.parse(item.beneficios);
          dadosPDF.beneficios = Array.isArray(beneficiosArray) 
            ? beneficiosArray.map((beneficio, index) => ({
                numero: beneficio.numero || (index + 1),
                texto: beneficio.texto || beneficio.toString()
              }))
            : [];
        } catch (e) {
          console.warn('Erro ao parsear benefícios:', e);
          dadosPDF.beneficios = [];
        }
      } else if (Array.isArray(item.beneficios)) {
        dadosPDF.beneficios = item.beneficios.map((beneficio, index) => ({
          numero: beneficio.numero || (index + 1),
          texto: beneficio.texto || beneficio.toString()
        }));
      }

      console.log('📊 Dados finais para o PDF:', {
        numeroProposta: dadosPDF.numeroProposta,
        nomeCliente: dadosPDF.nomeCliente,
        consultor: dadosPDF.consultor,
        descontoTarifa: dadosPDF.descontoTarifa, // Deve ser 0.2 (20%)
        descontoBandeira: dadosPDF.descontoBandeira, // Deve ser 0.2 (20%)
        inflacao: dadosPDF.inflacao, // Deve ser 0.02 (2%)
        tarifaTributos: dadosPDF.tarifaTributos, // Deve ser 0.8
        totalUCs: dadosPDF.ucs.length,
        primeiraUC: dadosPDF.ucs[0], // Para debug
        totalBeneficios: dadosPDF.beneficios.length
      });

      // ✅ VERIFICAR SE OS DADOS ESTÃO COMPLETOS
      if (dadosPDF.ucs.length === 0) {
        showNotification('Nenhuma unidade consumidora encontrada para esta proposta', 'warning');
        return;
      }

      if (dadosPDF.descontoTarifa === 0 || dadosPDF.descontoTarifa > 1) {
        console.warn('⚠️ Desconto tarifa parece incorreto:', dadosPDF.descontoTarifa);
      }

      // Gerar PDF via HTML
      const PDFGenerator = (await import('../services/pdfGenerator.js')).default;
      await PDFGenerator.baixarPDF(dadosPDF, true);

      // Mensagem de sucesso contextual
      const tipoInclusao = ucEspecifica
        ? `apenas para a UC: ${ucEspecifica.apelido}`
        : `com ${dadosPDF.ucs.length} UC${dadosPDF.ucs.length !== 1 ? 's' : ''}`;

      showNotification(
        `PDF da proposta ${item.numeroProposta} gerado com sucesso! (${tipoInclusao})`,
        'success'
      );
      
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      showNotification(`Erro ao gerar PDF: ${error.message}`, 'error');
    }
  };

  const removerItem = async (index) => {
    const item = dadosFiltrados[index];
    const confirmacao = window.confirm(
      `Tem certeza que deseja cancelar a proposta de ${item.nomeCliente} (${item.apelido || item.numeroUC})?`
    );
    
    if (!confirmacao) {
      return;
    }

    setLoading(true); // ← ADICIONAR loading no início

    try {
      const propostaId = item.propostaId || item.id;
      
      if (!propostaId) {
        showNotification('ID da proposta não encontrado', 'error');
        return;
      }

      console.log('🗑️ Removendo proposta com ID:', propostaId);

      // ✅ CANCELAR UC ESPECÍFICA - Alterar status da UC para "Cancelada"
      console.log('📝 Cancelando UC específica...');
      await storageService.atualizarProspec(propostaId, { 
        propostaId: propostaId,
        numeroUC: item.numeroUC || item.numero_unidade,
        status: 'Cancelada'
      });

      // ✅ Refresh automático após cancelar
      console.log('🔄 Atualizando dados automaticamente após cancelamento...');
      
      // Atualizar propostas (força reload)
      await loadPropostas(1, propostas.filters, true);
      
      // Atualizar controle também (força reload)  
      await loadControle(1, {}, true);
      
      showNotification('Proposta cancelada com sucesso!', 'success');
      
    } catch (error) {
      console.error('❌ Erro ao remover:', error);
      showNotification('Erro ao remover: ' + error.message, 'error');
    } finally {
      setLoading(false); // ← ADICIONAR loading no finally
    }
  };

  const criarNovaProposta = () => {
    navigate('/nova-proposta');
  };

  const abrirModalExportacao = () => {
    setModalExportacao(true);
  };

  const executarExportacao = async (filtros) => {
    try {
      setLoading(true);
      showNotification('Preparando exportação Excel da prospecção...', 'info');

      // Buscar todos os dados conforme permissão do usuário
      let todosOsDados;
      if (user?.role === 'admin' || user?.role === 'analista') {
        todosOsDados = await storageService.getProspec();
      } else {
        const dadosCompletos = await storageService.getProspec();
        todosOsDados = dadosCompletos;
      }

      // Transformar dados para formato esperado pelo Excel
      const dadosParaExportacao = todosOsDados.map(item => ({
        propostaId: item.id,
        numeroProposta: item.numeroProposta || item.numero_proposta,
        data: item.data || item.dataProposta || item.data_proposta,
        nomeCliente: item.nomeCliente || item.nome_cliente,
        consultor: item.consultor,
        status: item.status || item.status_proposta,
        numeroUC: item.numeroUC || item.numero_uc || item.numero_unidade,
        apelido: item.apelido || item.apelido_uc,
        consumoMedio: item.consumoMedio || item.consumo_medio,
        valorFatura: item.valorFatura || item.valor_fatura || item.valor_uc,
        descontoTarifa: item.descontoTarifa || item.desconto_tarifa,
        descontoBandeira: item.descontoBandeira || item.desconto_bandeira,
        observacoes: item.observacoes,
        beneficios: item.beneficios,
        recorrencia: item.recorrencia
      }));

      const resultado = await exportExcelService.exportarProspecParaExcel(dadosParaExportacao, filtros);
      showNotification(`Exportação Excel concluída! ${resultado.totalRegistros} registros exportados em ${resultado.arquivo}`, 'success');
      console.log('Dados prospecção:', dadosParaExportacao[0]);
    } catch (error) {
      console.error('❌ Erro na exportação:', error);
      showNotification(`Erro na exportação: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshDados = useCallback(() => {
    console.log('🔄 Refresh manual dos dados');
    loadPropostas(1, propostas.filters, true); // forceReload = true
  }, [loadPropostas, propostas.filters]);
  

  // Verificar se é admin para mostrar ações de admin
  const isAdminOrAnalista = user?.role === 'admin' || user?.role === 'analista';
  
  return (
    <div className="page-container">
      <div className="container">
        <Header 
          title="Gerenciamento de Propostas"  
        />
        <Navigation />

        {/* Estatísticas Rápidas */}
        <section className="quick-stats">
          <div className="stat-card">
            <div className="stat-icon">
              <FileText size={24} style={{ color: '#f0f0f0', opacity: 0.8 }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">Total</span>
              <span className="stat-value">{dadosFiltrados.length}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Clock size={24} style={{ color: '#f0f0f0', opacity: 0.8 }} />
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {(() => {
                  const aguardando = dadosFiltrados.filter(item => (item.status || 'Aguardando') === 'Aguardando');
                  const somaKwh = aguardando.reduce((acc, item) => acc + (parseFloat(item.media) || 0), 0);
                  return somaKwh.toLocaleString('pt-BR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }) + ' kWh';
                })()}
              </span>
              <span className="stat-label-small">
                {(() => {
                  const quantidade = dadosFiltrados.filter(item => (item.status || 'Aguardando') === 'Aguardando').length;
                  return `${quantidade} ${quantidade === 1 ? 'Proposta' : 'Propostas'} Aguardando`;
                })()}
              </span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} style={{ color: '#f0f0f0', opacity: 0.8 }} />
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {(() => {
                  const fechadas = dadosFiltrados.filter(item => (item.status || 'Aguardando') === 'Fechada');
                  const somaKwh = fechadas.reduce((acc, item) => acc + (parseFloat(item.media) || 0), 0);
                  return somaKwh.toLocaleString('pt-BR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }) + ' kWh';
                })()}
              </span>
              <span className="stat-label-small">
                {(() => {
                  const quantidade = dadosFiltrados.filter(item => (item.status || 'Aguardando') === 'Fechada').length;
                  return `${quantidade} ${quantidade === 1 ? 'Proposta' : 'Propostas'} Fechadas`;
                })()}
              </span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp size={24} style={{ color: '#f0f0f0', opacity: 0.8 }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">Valor Médio</span>
              <span className="stat-value">
                {dadosFiltrados.length > 0 
                  ? Math.round(dadosFiltrados.reduce((acc, item) => acc + (parseFloat(item.media) || 0), 0) / dadosFiltrados.length).toLocaleString('pt-BR')
                  : '0'} kWh
              </span>
            </div>
          </div>
        </section>

        {/* Filtros e Controles */}
        <section className="filters-section">
          <div className="filters-container">
            <div className="filters-grid">
              <div className="filter-group">
                <label>Buscar</label>
                <input
                  type="text"
                  placeholder="Buscar cliente, proposta, UC..."
                  value={filtros.busca}
                  onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                />
              </div>
              
              <div className="filter-group">
                <label>Consultor</label>
                <select
                  value={filtros.consultor}
                  onChange={(e) => setFiltros({...filtros, consultor: e.target.value})}
                >
                  <option value="">Todos</option>
                  {consultoresUnicos.map(consultor => (
                    <option key={consultor} value={consultor}>{consultor}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Status</label>
                <select
                  value={filtros.status}
                  onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                >
                  <option value="">Todos</option>
                  <option value="Aguardando">Aguardando</option>
                  <option value="Fechada">Fechadas</option>  {/* ✅ CORRIGIDO: value="Fechada" */}
                  <option value="Cancelada">Canceladas</option>
                  <option value="Recusada">Recusadas</option>
                </select>
              </div>
            </div>

            <div className="actions-container">
              <button onClick={limparFiltros} className="btn btn-secondary">
                Limpar Filtros
              </button>
              <button 
                onClick={refreshDados}
                className="btn btn-secondary"
                disabled={propostas.loading}
                title="Atualizar dados"
              >
                {propostas.loading ? '🔄' : '⟳'} Atualizar
              </button>
              <button onClick={criarNovaProposta} className="btn btn-secondary">
                <Plus size={16} />
                Nova Proposta
              </button>
              <button
                onClick={abrirModalExportacao}  // ← TROCAR DE exportarDados
                className="btn btn-primary"
                disabled={dadosFiltrados.length === 0}
                style={{ color: 'white' }}
              >
                <Download size={16} />
                  Exportar Excel
              </button>
            </div>
          </div>
        </section>

        {/* Tabela */}
        <section className="table-section">
          <div className="table-container">
            <div className="table-header">
              <h2><FileText /> Propostas <span className="table-count">{dadosFiltrados.length}</span></h2>
            </div>

            {propostas.loading && propostas.data.length === 0 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Carregando propostas...</p>
              </div>
            ) : dadosFiltrados.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h3>Nenhuma proposta encontrada</h3>
                <p>Não há propostas que correspondam aos filtros aplicados.</p>
                <button onClick={criarNovaProposta} className="btn btn-secondary">
                  <Plus size={16} />
                  Criar Nova Proposta
                </button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Nº Proposta</th>
                      <th>Data</th>
                      <th>Apelido</th>
                      <th>UC</th>
                      <th>Média</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosFiltrados.map((item, index) => (
                      <tr key={item.id} className={item.status === 'Cancelada' ? 'linha-cancelada' : ''}>
                        <td>{item.nomeCliente || '-'}</td>
                        <td>
                          <span className="numero-proposta">
                            {item.numeroProposta || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="data">
                            {item.data ? (() => {
                              try {
                                // Se a data vem no formato YYYY-MM-DD, converter diretamente
                                if (item.data.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                  const [ano, mes, dia] = item.data.split('-');
                                  return `${dia}/${mes}/${ano}`;
                                }
                                // Para outros formatos, usar Date normalmente
                                const dataObj = new Date(item.data);
                                return dataObj.toLocaleDateString('pt-BR');
                              } catch {
                                return item.data;
                              }
                            })() : '-'}
                          </span>
                        </td>
                        <td>{item.apelido || '-'}</td>
                        <td>{item.numeroUC || '-'}</td>
                        <td>
                          <span className="valor">
                            {item.media ? parseFloat(item.media).toLocaleString('pt-BR') : '0'} kWh
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${(item.status || 'Aguardando').toLowerCase()}`}>
                              {item.status || 'Aguardando'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button 
                              onClick={() => visualizarItem(index)} 
                              className="btn-icon view"
                              title="Visualizar"
                            >
                              <Eye size={16} />
                            </button>
                            <button 
                              onClick={() => editarItem(index)} 
                              className="btn-icon edit"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              onClick={() => gerarPDFProposta(item)} 
                              className="btn-icon pdf"
                              title="Gerar PDF"
                            >
                              <FileText size={16} />
                            </button>
                            <button 
                              onClick={() => removerItem(index)} 
                              className="btn-icon delete"
                              title="Excluir"
                              disabled={loading} // ← Desabilitar durante loading
                            >
                              {loading ? (
                                <div className="loading-spinner-inline"></div>
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Modal de Visualização - Para todos os perfis */}
        {modalVisualizacao.show && (
          <ModalVisualizacao 
            item={modalVisualizacao.item}
            user={user}
            onClose={() => setModalVisualizacao({ show: false, item: null })}
          />
        )}

        {/* Modal de Edição - Para todos os perfis */}
        {modalEdicao.show && (
          <ModalEdicao
            item={modalEdicao.item}
            onSave={salvarEdicao}
            onClose={() => setModalEdicao({ show: false, item: null, index: -1 })}
            loading={loading}
            setLoading={setLoading} 
            consultoresDisponiveis={consultoresDisponiveis}
          />
        )}
        {/* Modal de Filtros para Exportação */}
        <ModalFiltrosExportacao
          isOpen={modalExportacao}
          onClose={() => setModalExportacao(false)}
          onExportar={executarExportacao}
          tipo="prospec"
          consultores={consultoresUnicos}
        />
      </div>
    </div>
  );
};

// Componente Modal de Visualização - Disponível para todos os perfis
const ModalVisualizacao = ({ item, user, onClose }) => {
  const formatarPercentualModal = (valor) => {
    if (!valor && valor !== 0) return '0.0%';
    
    // Se já é string com %, extrair número e formatar
    if (typeof valor === 'string' && valor.includes('%')) {
      const numero = parseFloat(valor.replace('%', ''));
      return `${numero.toFixed(1)}%`;
    }
    
    // Se é número, usar direto (SEM multiplicação)
    const numero = parseFloat(valor);
    if (isNaN(numero)) return '0.0%';
    
    console.log('🔍 formatarPercentualModal:', { 
      valor, 
      numero, 
      resultado: `${numero.toFixed(1)}%` 
    });
    
    return `${numero.toFixed(1)}%`;
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      // Se a data vier no formato ISO (2025-07-30T03:00:00.000Z), converter
      const dataObj = new Date(data);
      return dataObj.toLocaleDateString('pt-BR');
    } catch (error) {
      return data; // Retorna o valor original se não conseguir converter
    }
  };

  // Lista de benefícios padrão com seus textos originais
  const beneficiosPadrao = [
    "Os benefícios economicos foram calculados com base nas tarifas de energia, sem impostos",
    "A titularidade da fatura será transferida para o Consorcio Clube Aupus", 
    "A Aupus Energia fornecerá consultoria energética para o condomínio",
    "Todo o processo será conduzido pela Aupus Energia, não se preocupe",
    "Você irá pagar DOIS boletos, sendo um boleto mínimo para Equatorial e o outro sendo Aluguel da Usina para Aupus Energial",
    "Contamos com uma moderna plataforma para te oferecer uma experiencia única!",
    "A proposta se aplica para todos os condominos que tiverem interesse",
    "Desconto em DOBRO no primeiro mês!!"
  ];

  // Obter benefícios reais marcados na proposta
  const obterBeneficiosReais = () => {
    const beneficiosAplicaveis = [];
    
    // Verificar se temos benefícios salvos na proposta
    if (item.beneficios && typeof item.beneficios === 'object') {
      // Se os benefícios estão salvos como objeto
      for (let i = 1; i <= 9; i++) {
        if (item.beneficios[`beneficio${i}`]) {
          let textoBeneficio = beneficiosPadrao[i - 1];
          
          // Personalizar benefícios 1 e 2 com os valores reais
          if (i === 1 && item.descontoTarifa) {
            const desconto = parseFloat(item.descontoTarifa).toFixed(1); // ✅ SEM MULTIPLICAÇÃO
            textoBeneficio = `A Aupus Energia irá oferecer uma economia de até ${desconto}% no valor da energia elétrica, sem impostos`;
          }
          if (i === 2 && item.descontoBandeira) {
            const desconto = parseFloat(item.descontoBandeira).toFixed(1); // ✅ SEM MULTIPLICAÇÃO
            textoBeneficio = `A Aupus Energia irá oferecer uma economia de até ${desconto}% no valor referente à bandeira tarifária, sem impostos`;
          }
          
          beneficiosAplicaveis.push(textoBeneficio);
        }
      }
    } else {
      // Fallback: se não tem benefícios salvos, usar baseado nos descontos
      if (item.descontoTarifa && parseFloat(item.descontoTarifa) > 0) {
        const desconto = parseFloat(item.descontoTarifa).toFixed(1); // ✅ SEM MULTIPLICAÇÃO
        beneficiosAplicaveis.push(`A Aupus Energia irá oferecer uma economia de até ${desconto}% no valor da energia elétrica, sem impostos`);
      }

      if (item.descontoBandeira && parseFloat(item.descontoBandeira) > 0) {
        const desconto = parseFloat(item.descontoBandeira).toFixed(1); // ✅ SEM MULTIPLICAÇÃO
        beneficiosAplicaveis.push(`A Aupus Energia irá oferecer uma economia de até ${desconto}% no valor referente à bandeira tarifária, sem impostos`);
      }
      
      // Adicionar benefícios padrão comuns
      beneficiosAplicaveis.push("Isenção de taxa de adesão");
      beneficiosAplicaveis.push("Não há cobrança de taxa de cancelamento");
      beneficiosAplicaveis.push("Não há fidelidade contratual");
      beneficiosAplicaveis.push("O cliente pode cancelar a qualquer momento");
    }

    return beneficiosAplicaveis;
  };

  const beneficiosReais = obterBeneficiosReais();

  // ✅ CORREÇÃO: Verificar se deve mostrar recorrência (apenas admin e consultor)
  const mostrarRecorrencia = user?.role === 'admin' || user?.role === 'analista' || user?.role === 'consultor';

  return (
    <div className="common-modal-overlay">
      <div className="common-modal large">
        <div className="common-modal-header">
          <h2>
            <Eye size={20} />
            Detalhes da Proposta
          </h2>
          <button onClick={onClose} className="common-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="common-modal-content">
          <div className="proposta-details">
            {/* Informações principais - REORGANIZADA */}
            <div className="details-section">
              <h4>
                <FileText size={18} />
                Informações Principais
              </h4>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Cliente:</label>
                  <span>{item.nomeCliente || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Nº Proposta:</label>
                  <span className="numero-proposta">{item.numeroProposta || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Data:</label>
                  <span className="data">{formatarData(item.data)}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${item.status === 'Fechado' ? 'status-fechado' : 'status-aguardando'}`}>
                    {item.status || 'Aguardando'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Consultor:</label>
                  <span>{item.consultor || '-'}</span>
                </div>
                {/* ✅ CORREÇÃO: Mostrar recorrência apenas para admin e consultor */}
                {mostrarRecorrencia && (
                  <div className="detail-item">
                    <label>Recorrência:</label>
                    <span>{item.recorrencia || '-'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Informações da UC */}
            <div className="details-section">
              <h4>
                <TrendingUp size={18} />
                Informações da UC
              </h4>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Apelido:</label>
                  <span>{item.apelido || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Número UC:</label>
                  <span>{item.numeroUC || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Ligação:</label>
                  <span>{item.ligacao || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Média Consumo:</label>
                  <span className="valor">
                    {item.media ? parseFloat(item.media).toLocaleString('pt-BR') : '0'} kWh
                  </span>
                </div>
              </div>
            </div>

            {/* ✅ CORREÇÃO: Descontos e benefícios - Só mostrar para admin e consultor */}
            {mostrarRecorrencia && (
              <div className="details-section">
                <h4>
                  <DollarSign size={18} />
                  Descontos e Benefícios
                </h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Desconto Tarifa:</label>
                    <span className="desconto-valor">{formatarPercentualModal(item.descontoTarifa)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Desconto Bandeira:</label>
                    <span className="desconto-valor">{formatarPercentualModal(item.descontoBandeira)}</span>
                  </div>
                </div>
                
                {/* Lista de benefícios reais */}
                {beneficiosReais.length > 0 && (
                  <div className="beneficios-lista">
                    <h5>📝 Benefícios Inclusos:</h5>
                    <ul>
                      {beneficiosReais.map((beneficio, index) => (
                        <li key={index}>{beneficio}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* ✅ NOVA SEÇÃO: Observações (visível para todos) */}
            {item.observacoes && (
              <div className="details-section">
                <h4>📝 Observações</h4>
                <div className="observacoes-content">
                  <p>{item.observacoes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente Modal de Edição - ATUALIZADO com novos campos
const ModalEdicao = ({ item, onSave, onClose, loading, setLoading, consultoresDisponiveis }) => {
  const { user, getMyTeam } = useAuth(); 
  const { showNotification } = useNotification();
  const [statusDocumentos, setStatusDocumentos] = useState({}); 
  // ✅ Função para obter status específico da UC
  const getStatusDocumentoUC = (numeroUC) => {
    return statusDocumentos[numeroUC] || null;
  };

  // ✅ Função para definir status específico da UC
  const setStatusDocumentoUC = (numeroUC, status) => {
    setStatusDocumentos(prev => ({
      ...prev,
      [numeroUC]: status
    }));
  };
  const [dados, setDados] = useState({ 
    ...item,
    consultor_id: item.consultor_id || null,
    consultor: item.consultor || '',
    
    
    // ✅ EXTRAIR valores numéricos dos descontos na inicialização
    descontoTarifa: (() => {
      const valor = item.descontoTarifa || item.economia;
      if (typeof valor === 'string' && valor.includes('%')) {
        return parseFloat(valor.replace('%', ''));
      }
      return parseFloat(valor) || 20;
    })(),
    
    descontoBandeira: (() => {
      const valor = item.descontoBandeira || item.bandeira;
      if (typeof valor === 'string' && valor.includes('%')) {
        return parseFloat(valor.replace('%', ''));
      }
      return parseFloat(valor) || 20;
    })(),
        
    // ... resto dos campos permanecem iguais
    tipoDocumento: item.tipoDocumento || 'CPF',
    nomeRepresentante: item.nomeRepresentante || '',
    cpf: item.cpf || '',
    documentoPessoal: item.documentoPessoal || null,
    razaoSocial: item.razaoSocial || '',
    cnpj: item.cnpj || '',
    contratoSocial: item.contratoSocial || null,
    documentoPessoalRepresentante: item.documentoPessoalRepresentante || null,
    enderecoUC: item.enderecoUC || '',
    CEP_UC: item.CEP_UC || '',
    Bairro_UC: item.Bairro_UC || '',
    Cidade_UC: item.Cidade_UC || '',
    Estado_UC: item.Estado_UC || '',
    isArrendamento: item.isArrendamento || false,
    contratoLocacao: item.contratoLocacao || null,
    enderecoRepresentante: item.enderecoRepresentante || '',
    termoAdesao: item.termoAdesao || null,
    documentosExtras: item.documentosExtras || []
  });

  const buscarStatusDocumento = async () => {
    if (!item?.propostaId && !item?.id) return;
    
    const propostaId = item.propostaId || item.id?.split('-')[0];
    if (!propostaId) return;
    
    const numeroUC = dados.numeroUC || dados.numero_uc;
    if (!numeroUC) {
      console.log('⚠️ Sem numeroUC para buscar documento específico');
      return;
    }
    
    try {
      // ✅ CORREÇÃO: SEMPRE incluir numero_uc
      const statusUrl = `${process.env.REACT_APP_API_URL}/documentos/propostas/${propostaId}/status?numero_uc=${numeroUC}`;

      const response = await fetch(statusUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('aupus_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.documento) {
          // ✅ VERIFICAÇÃO DUPLA: garantir que é o documento da UC certa
          if (result.documento.numero_uc === numeroUC) {
            setStatusDocumentoUC(numeroUC, result.documento);
            console.log('📄 Status documento atualizado para UC:', numeroUC);
          } else {
            console.log('⚠️ Documento retornado é de UC diferente:', result.documento.numero_uc);
          }
        } else {
          // Se não encontrou documento, limpar status da UC
          setStatusDocumentoUC(numeroUC, null);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar status do documento:', error);
    }
  };

  
  const [faturaArquivo, setFaturaArquivo] = useState(null);

  useEffect(() => {
    // Mapear consultor nome para ID quando consultores carregarem
    if (consultoresDisponiveis.length > 0 && dados.consultor && !dados.consultor_id) {
      const consultorEncontrado = consultoresDisponiveis.find(c => c.name === dados.consultor);
      if (consultorEncontrado) {
        setDados(prev => ({
          ...prev,
          consultor_id: consultorEncontrado.id
        }));
        console.log('🔗 Consultor mapeado:', {
          nome: dados.consultor,
          id: consultorEncontrado.id
        });
      }
    }
  }, [consultoresDisponiveis, dados.consultor, dados.consultor_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('🔵 INÍCIO handleSubmit - Botão clicado');
    console.log('🔍 Estado loading atual:', loading);

    if (loading) {
      console.log('⚠️ Já está carregando, ignorando submit');
      return;
    }

    if (dados.consultor_id === null || dados.consultor_id === '') {
      dados.recorrencia = 0;
      dados.consultor_id = null;
      dados.consultor = 'Sem consultor';
    }

    // Validação apenas se status for "Fechado"
    if (dados.status === 'Fechado') {
      const camposObrigatorios = [];
      
      if (!dados.nomeCliente) camposObrigatorios.push('Nome do Cliente');
      if (!dados.apelido) camposObrigatorios.push('Apelido UC');
      if (!dados.numeroUC) camposObrigatorios.push('Número UC');
      if (!dados.consultor_id && !dados.consultor) camposObrigatorios.push('Consultor Responsável');
      if (!dados.enderecoUC) camposObrigatorios.push('Endereço da UC');
      if (!dados.CEP_UC) camposObrigatorios.push('CEP da UC');
      if (!dados.Bairro_UC) camposObrigatorios.push('Bairro da UC');
      if (!dados.Cidade_UC) camposObrigatorios.push('Cidade da UC');
      if (!dados.Estado_UC) camposObrigatorios.push('Estado da UC');
      if (!dados.logradouroUC) camposObrigatorios.push('Logradouro da UC');
      if (!dados.enderecoRepresentante) camposObrigatorios.push('Endereço do Representante');
      
      if (dados.tipoDocumento === 'CPF') {
        if (!dados.nomeRepresentante) camposObrigatorios.push('Nome do Representante');
        if (!dados.cpf) camposObrigatorios.push('CPF');
      } else {
        if (!dados.razaoSocial) camposObrigatorios.push('Razão Social');
        if (!dados.cnpj) camposObrigatorios.push('CNPJ');
        if (!dados.nomeRepresentante) camposObrigatorios.push('Nome do Representante da Empresa');
      }
      
      if (camposObrigatorios.length > 0) {
        alert(`Para fechar a proposta, preencha os seguintes campos:\n\n• ${camposObrigatorios.join('\n• ')}`);
        return;
      }
    }

    console.log('🔄 Definindo loading = true');
    setLoading(true);
    console.log('🔍 Loading definido, continuando...');
    
    try {
      // ✅ FAZER UPLOAD DA FATURA PRIMEIRO (DENTRO DO MODAL) - CORRIGIDO
      if (faturaArquivo && (item.numeroUC || item.numero_unidade)) {
        try {
          console.log('📤 Fazendo upload da fatura da UC...');
          
          const propostaId = item.propostaId || item.id?.split('-')[0];
          const numeroUC = item.numeroUC || item.numero_unidade;
          
          console.log('🔍 DEBUG - Dados do upload:', {
            propostaId,
            numeroUC,
            arquivo: faturaArquivo.name,
            tamanho: faturaArquivo.size,
            tipo: faturaArquivo.type
          });
          
          const formData = new FormData();
          formData.append('arquivo', faturaArquivo);
          formData.append('numeroUC', String(numeroUC));
          formData.append('tipoDocumento', 'faturaUC');

          const response = await fetch(`${process.env.REACT_APP_API_URL}/propostas/${propostaId}/upload-documento`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('aupus_token')}`
            },
            body: formData
          });

          // ✅ CORREÇÃO: Verificar se a resposta é JSON válida
          console.log('📥 Response status:', response.status);
          console.log('📥 Response headers:', Object.fromEntries(response.headers));
          
          if (!response.ok) {
            // ✅ CORREÇÃO: Tentar obter texto da resposta para debug
            let errorText;
            try {
              errorText = await response.text();
              console.error('❌ Resposta de erro do servidor:', errorText);
            } catch (textError) {
              console.error('❌ Não foi possível ler resposta de erro:', textError);
              errorText = `HTTP ${response.status}`;
            }
            
            throw new Error(`Erro no upload da fatura: HTTP ${response.status} - ${errorText}`);
          }

          // ✅ CORREÇÃO: Verificar se a resposta contém JSON válido
          const contentType = response.headers.get('content-type');
          console.log('📋 Content-Type da resposta:', contentType);
          
          if (!contentType || !contentType.includes('application/json')) {
            console.warn('⚠️ Resposta não é JSON, tentando ler como texto...');
            const responseText = await response.text();
            console.error('❌ Resposta não-JSON:', responseText);
            throw new Error(`Servidor retornou resposta inválida (não-JSON): ${responseText.substring(0, 200)}...`);
          }

          // ✅ CORREÇÃO: Tentar fazer parse do JSON com tratamento de erro
          let result;
          try {
            const responseText = await response.text();
            console.log('📄 Resposta raw do servidor:', responseText.substring(0, 500));
            
            if (!responseText.trim()) {
              throw new Error('Resposta vazia do servidor');
            }
            
            result = JSON.parse(responseText);
            console.log('✅ JSON parseado com sucesso:', result);
          } catch (jsonError) {
            console.error('❌ Erro ao fazer parse do JSON:', jsonError);
            console.error('❌ Resposta que causou erro:', await response.text());
            throw new Error(`Resposta JSON inválida do servidor: ${jsonError.message}`);
          }

          // ✅ Validar estrutura da resposta
          if (!result || typeof result !== 'object') {
            throw new Error('Resposta do servidor não é um objeto válido');
          }

          if (!result.success) {
            throw new Error(result.message || 'Upload falhou sem mensagem de erro');
          }

          console.log('✅ Fatura da UC enviada com sucesso:', result.nomeArquivo);
          showNotification(`Fatura da UC ${numeroUC} enviada com sucesso!`, 'success');
          setFaturaArquivo(null);
          
        } catch (error) {
          console.error('❌ Erro ao enviar fatura da UC:', error);
          console.error('❌ Stack trace:', error.stack);
          showNotification(`Erro ao enviar fatura da UC: ${error.message}`, 'error');
          setLoading(false);
          return;
        }
      }

      // ✅ SALVAR OS DADOS DA PROPOSTA
      console.log('💾 Iniciando salvamento dos dados da proposta...');
      console.log('📊 Dados que serão enviados:', dados);
      console.log('🔗 Função onSave:', typeof onSave);

      if (typeof onSave !== 'function') {
        throw new Error('onSave não é uma função válida');
      }

      await onSave(dados);
      console.log('✅ onSave executada com sucesso');

    } catch (error) {
      console.error('❌ Erro detalhado ao salvar proposta:', {
        message: error.message,
        stack: error.stack,
        error: error
      });
      showNotification(`Erro ao salvar: ${error.message || 'Erro desconhecido'}`, 'error');
    } finally {
      console.log('🔵 FIM handleSubmit - setLoading(false)');
      setLoading(false);
    }
  };
  
  const handleApiResponse = async (response, operacao = 'operação') => {
    console.log(`📥 Response ${operacao}:`, {
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type'),
      url: response.url
    });

    // Verificar status HTTP
    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
      } catch {
        errorText = `HTTP ${response.status} ${response.statusText}`;
      }
      
      console.error(`❌ Erro HTTP ${operacao}:`, errorText);
      throw new Error(`Erro na ${operacao}: ${errorText}`);
    }

    // Verificar Content-Type
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error(`❌ Resposta não-JSON ${operacao}:`, responseText);
      throw new Error(`Servidor retornou resposta não-JSON para ${operacao}`);
    }

    // Parse JSON
    const responseText = await response.text();
    if (!responseText.trim()) {
      throw new Error(`Resposta vazia do servidor para ${operacao}`);
    }

    try {
      const result = JSON.parse(responseText);
      console.log(`✅ JSON parseado ${operacao}:`, result);
      return result;
    } catch (jsonError) {
      console.error(`❌ Erro JSON ${operacao}:`, jsonError);
      console.error('❌ Resposta que causou erro:', responseText.substring(0, 500));
      throw new Error(`JSON inválido na ${operacao}: ${jsonError.message}`);
    }
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const handleFileChange = (campo, file) => {
    setDados({...dados, [campo]: file});
  };

  const handleFaturaUpload = (file) => {
    console.log('📁 Fatura selecionada:', file?.name);
    console.log('🔍 Detalhes do arquivo:', {
      name: file?.name,
      type: file?.type,
      size: file?.size
    });
    
    if (file) {
      // ✅ CORREÇÃO: Lista mais abrangente de MIME types
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',          // Alguns browsers podem reportar assim
        'image/pjpeg',        // Internet Explorer
        'image/png',
        'image/x-png'         // Variação do PNG
      ];
      
      // ✅ CORREÇÃO: Validação também por extensão como fallback
      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      const maxSize = 10 * 1024 * 1024; // ✅ CORREÇÃO: 10MB (não 100MB)
      
      // ✅ VALIDAÇÃO MELHORADA: MIME type OU extensão
      const isMimeTypeValid = allowedTypes.includes(file.type);
      const isFileValid = isMimeTypeValid || hasValidExtension;
      
      console.log('🔍 Validação do arquivo:', {
        mimeType: file.type,
        isMimeTypeValid,
        hasValidExtension,
        isFileValid,
        size: file.size,
        maxSize
      });
      
      if (!isFileValid) {
        console.error('❌ Tipo de arquivo inválido:', {
          type: file.type,
          name: file.name,
          allowedTypes,
          allowedExtensions
        });
        alert(`Apenas arquivos PDF, JPG e PNG são permitidos para faturas. Arquivo enviado: ${file.type}`);
        return;
      }
          
      if (file.size > maxSize) {
        console.error('❌ Arquivo muito grande:', {
          size: file.size,
          maxSize,
          sizeMB: (file.size / 1024 / 1024).toFixed(2)
        });
        alert('Arquivo muito grande. Tamanho máximo: 10MB');
        return;
      }
      
      console.log('✅ Arquivo validado com sucesso');
    }
    
    setFaturaArquivo(file);
  };

  const debugFileInfo = (file) => {
    if (!file) return;
    
    console.log('🔍 DEBUG - Informações completas do arquivo:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      lastModifiedDate: new Date(file.lastModified),
      // Extensão extraída do nome
      extension: file.name.split('.').pop()?.toLowerCase(),
      // Verificar se é realmente uma imagem
      isImageFile: file.type.startsWith('image/'),
      // Tamanho em MB
      sizeMB: (file.size / 1024 / 1024).toFixed(2) + ' MB'
    });
    
    // Verificar se o browser reporta MIME type corretamente
    if (file.type === '' || !file.type) {
      console.warn('⚠️ MIME type vazio - browser não conseguiu detectar');
    }
    
    if (file.name.toLowerCase().endsWith('.jpg') && !file.type.includes('jpeg')) {
      console.warn('⚠️ Arquivo .jpg mas MIME type não contém "jpeg":', file.type);
    }
  };

  const handleTipoDocumentoChange = (tipo) => {
    setDados({
      ...dados, 
      tipoDocumento: tipo,
      // Limpar campos do tipo não selecionado
      ...(tipo === 'CPF' ? {
        razaoSocial: '',
        cnpj: '',
        contratoSocial: null,
        documentoPessoalRepresentante: null
      } : {
        nomeRepresentante: '',
        cpf: '',
        documentoPessoal: null
      })
    });
  };
  
  return (
    <div className="common-modal-overlay">
      {(() => {
        console.log('🎯 RENDER Modal - consultoresDisponiveis:', consultoresDisponiveis.length, consultoresDisponiveis);
        console.log('🎯 RENDER Modal - dados.consultor_id:', dados.consultor_id);
        return null;
      })()}

      <div className="common-modal large">
        <div className="common-modal-header">
          <h2>
            <Edit size={20} />
            Editar Proposta
          </h2>
          <button onClick={handleClose} className="common-close-btn">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="common-modal-content">
          {/* Informações Básicas */}
          <div className="secao-modal">
            <h4 className="titulo-secao">
              <FileText size={18} />
              Informações Básicas
            </h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>Nome do Cliente</label>
                <input
                  type="text"
                  value={dados.nomeCliente || ''}
                  onChange={(e) => setDados({...dados, nomeCliente: formatarPrimeiraMaiuscula(e.target.value, true)})}
                />
              </div>
              <div className="form-group">
                <label>Apelido UC</label>
                <input
                  type="text"
                  value={dados.apelido || ''}
                  onChange={(e) => setDados({...dados, apelido: formatarPrimeiraMaiuscula(e.target.value, true)})}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Número UC</label>
                <input
                  type="text"
                  value={dados.numeroUC || ''}
                  onChange={(e) => setDados({...dados, numeroUC: e.target.value})}
                  readOnly
                  className="input-readonly"
                />
              </div>
              <div className="form-group">
                <label>Ligação</label>
                <select
                  value={dados.ligacao || ''}
                  onChange={(e) => {
                    console.log('🔧 Alterando ligação:', e.target.value);
                    setDados({...dados, ligacao: e.target.value});
                  }}
                >
                  <option value="">Selecione...</option>
                  <option value="MONOFÁSICA">MONOFÁSICA</option>
                  <option value="BIFÁSICA">BIFÁSICA</option>
                  <option value="TRIFÁSICA">TRIFÁSICA</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Desconto Tarifa (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  name="descontoTarifa"
                  value={(() => {
                  // ✅ PRESERVAR valor real digitado ou extrair do valor inicial
                  const valor = dados.descontoTarifa;
                  if (typeof valor === 'string' && valor.includes('%')) {
                    return parseFloat(valor.replace('%', ''));
                  }
                  return parseFloat(valor) || '';
                })()} 
                onChange={(e) => {
                  // ✅ GUARDAR apenas o número, sem formatação
                  const numeroLimpo = parseFloat(e.target.value) || 0;
                  console.log('🔧 Desconto Tarifa alterado:', numeroLimpo);
                  setDados({...dados, descontoTarifa: numeroLimpo});
                }} 
                />
              </div>
              <div className="form-group">
                <label>Desconto Bandeira (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  name="descontoBandeira"
                  value={(() => {
                  const valor = dados.descontoBandeira;
                  if (typeof valor === 'string' && valor.includes('%')) {
                    return parseFloat(valor.replace('%', ''));
                  }
                  return parseFloat(valor) || '';
                })()} 
                onChange={(e) => {
                  // ✅ GUARDAR apenas o número, sem formatação
                  const numeroLimpo = parseFloat(e.target.value) || 0;
                  console.log('🔧 Desconto Bandeira alterado:', numeroLimpo);
                  setDados({...dados, descontoBandeira: numeroLimpo});
                }} 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Consultor Responsável</label>
                {!consultoresDisponiveis || consultoresDisponiveis.length === 0 ? (
                  <div style={{padding: '8px', color: '#666'}}>Carregando consultores...</div>
                ) : (
                  <select
                    value={dados.consultor_id === null ? 'null' : (dados.consultor_id || '')}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedId = selectedValue === 'null' ? null : (selectedValue === '' ? null : selectedValue);
                      const selectedConsultor = consultoresDisponiveis.find(c => c.id === selectedId);
                      
                      console.log('🔧 Alterando consultor:', {
                        value: selectedValue,
                        id: selectedId,
                        nome: selectedConsultor?.name
                      });
                      
                      if (selectedId === null) {
                        setDados({
                          ...dados, 
                          consultor_id: null,
                          consultor: 'Sem consultor',
                          recorrencia: 0
                        });
                      } else {
                        setDados({
                          ...dados, 
                          consultor_id: selectedId,
                          consultor: selectedConsultor.name,
                          recorrencia: dados.recorrencia || 1
                        });
                      }
                    }}
                  >
                    <option value="">Selecione um consultor...</option>
                    {/* ✅ APENAS ADMIN vê esta opção */}
                    {(user?.role === 'admin' || user?.role === 'analista') && (
                      <option value="null">Sem consultor (AUPUS direto)</option>
                    )}
                    {consultoresDisponiveis
                      .filter(consultor => consultor.id !== null)
                      .map((consultor, index) => {
                        // Verificar se há nomes duplicados para mostrar email
                        const nomeDuplicado = consultoresDisponiveis.filter(c => c.name === consultor.name && c.id !== null).length > 1;
                        const displayText = nomeDuplicado && consultor.email
                          ? `${consultor.name} (${consultor.email})`
                          : consultor.name;

                        return (
                          <option key={consultor.id || `consultor-${index}`} value={consultor.id}>
                            {displayText}
                          </option>
                        );
                      })}
                  </select>
                )}
              </div>
              <div className="form-group">
                <label>Média (kWh)</label>
                <input
                  type="number"
                  min="250"
                  value={dados.media || ''}
                  onChange={(e) => setDados({...dados, media: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
          </div>
          
          {/* Tipo de Documento */}
          <div className="secao-modal">
            <h4 className="titulo-secao">
              <FileText size={18} />
              Documentação
            </h4>
            <div className="form-group">
              <label>Fatura de Energia da UC</label>
              
              {/* ✅ NOVO INPUT DE UPLOAD */}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFaturaUpload(e.target.files[0])}
              />
                            
              {/* ✅ MOSTRAR ARQUIVO SELECIONADO PARA UPLOAD */}
              {faturaArquivo && (
                <div className="arquivo-selecionado">
                  <span className="arquivo-info" title={faturaArquivo.name}>
                    Novo arquivo: {faturaArquivo.name.length > 30 ? faturaArquivo.name.substring(0, 30) + '...' : faturaArquivo.name}
                  </span>
                  <button
                    type="button"
                    className="btn-remover-arquivo"
                    onClick={() => setFaturaArquivo(null)}
                    title="Remover arquivo selecionado"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              {/* ✅ MOSTRAR FATURA EXISTENTE (se houver) */}
              {(() => {
                const documentacao = dados.documentacao || item.documentacao || {};
                const faturas_ucs = documentacao.faturas_ucs || {};
                const numeroUC = item.numeroUC || item.numero_unidade;
                const faturaExistente = faturas_ucs[numeroUC];
                
                if (faturaExistente) {
                  return (
                    <div className="arquivo-existente">
                      <span className="arquivo-info" title={faturaExistente}>
                        Fatura atual: {faturaExistente.length > 30 ? faturaExistente.substring(0, 30) + '...' : faturaExistente}
                      </span>
                      <div className="arquivo-acoes">
                        <button
                          type="button"
                          className="btn-visualizar-doc"
                          onClick={() => window.open(`${process.env.REACT_APP_API_URL.replace(/\/api$/, '') }/storage/propostas/faturas/${faturaExistente}`, '_blank')}
                          title="Visualizar fatura"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <span className="sem-arquivo">Nenhuma fatura carregada para esta UC</span>
                  );
                }
              })()}
            </div>

            {/* Seletor de Tipo de Documento */}
            <div className="document-type-selector">
              <label className="radio-option">
                <input 
                  type="radio" 
                  name="tipoDocumento" 
                  value="CPF" 
                  checked={dados.tipoDocumento === 'CPF'}
                  onChange={() => handleTipoDocumentoChange('CPF')}
                />
                <span>Pessoa Física (CPF)</span>
              </label>
              <label className="radio-option">
                <input 
                  type="radio" 
                  name="tipoDocumento" 
                  value="CNPJ" 
                  checked={dados.tipoDocumento === 'CNPJ'}
                  onChange={() => handleTipoDocumentoChange('CNPJ')}
                />
                <span>Pessoa Jurídica (CNPJ)</span>
              </label>
            </div>

            {/* Campos CPF */}
            {dados.tipoDocumento === 'CPF' && (
              <div className="document-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome do Representante Legal</label>
                    <input
                      type="text"
                      value={dados.nomeRepresentante || ''}
                      onChange={(e) => setDados({...dados, nomeRepresentante: e.target.value})}
                      placeholder="Nome completo do representante"
                    />
                  </div>
                  <div className="form-group">
                    <label>CPF</label>
                    <input
                      type="text"
                      value={dados.cpf || ''}
                      onChange={(e) => setDados({...dados, cpf: e.target.value})}
                      placeholder="000.000.000-00"
                    />
                  </div>
                </div>
                
                {/* Documento Pessoal */}
                <div className="form-row">
                  <div className="form-group file-group">
                    <label>Documento Pessoal (RG/CNH)</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange('documentoPessoal', e.target.files[0])}
                    />
                    {dados.documentoPessoal ? (
                      <div className="arquivo-existente">
                        <span className="arquivo-info" title={typeof dados.documentoPessoal === 'string' ? dados.documentoPessoal : dados.documentoPessoal.name}>
                          {typeof dados.documentoPessoal === 'string' ? 
                            `Doc: ${dados.documentoPessoal.length > 30 ? dados.documentoPessoal.substring(0, 30) + '...' : dados.documentoPessoal}` : 
                            `Arquivo: ${dados.documentoPessoal.name.length > 30 ? dados.documentoPessoal.name.substring(0, 30) + '...' : dados.documentoPessoal.name}`}
                        </span>
                        {typeof dados.documentoPessoal === 'string' && (
                          <BotoesDocumento 
                            nomeArquivo={dados.documentoPessoal} 
                            tipoArquivo="documento pessoal"
                          />
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
            
            {/* Campos CNPJ - MANTÉM O MESMO */}
            {dados.tipoDocumento === 'CNPJ' && (
              <div className="document-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label>Razão Social</label>
                    <input
                      type="text"
                      value={dados.razaoSocial || ''}
                      onChange={(e) => setDados({...dados, razaoSocial: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>CNPJ</label>
                    <input
                      type="text"
                      value={dados.cnpj || ''}
                      onChange={(e) => setDados({...dados, cnpj: e.target.value})}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome do Representante da Empresa</label>
                    <input
                      type="text"
                      value={dados.nomeRepresentante || ''}
                      onChange={(e) => setDados({...dados, nomeRepresentante: e.target.value})}
                      placeholder="Nome completo do representante legal"
                    />
                  </div>
                  <div className="form-group">
                    {/* Espaço vazio para manter layout */}
                  </div>
                </div>
                
                {/* Arquivos CNPJ */}
                <div className="form-row">
                  <div className="form-group file-group">
                    <label>Contrato Social da Empresa</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange('contratoSocial', e.target.files[0])}
                    />
                    {dados.contratoSocial ? (
                      <div className="arquivo-existente">
                        <span className="arquivo-info">Contrato Social</span>
                        <BotoesDocumento 
                          nomeArquivo={dados.contratoSocial} 
                          tipoArquivo="contrato social"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="form-group file-group">
                    <label>Documento do Representante</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange('documentoPessoalRepresentante', e.target.files[0])}
                    />
                    {dados.documentoPessoalRepresentante ? (
                      <div className="arquivo-existente">
                        <span className="arquivo-info">Doc. Representante</span>
                        <BotoesDocumento 
                          nomeArquivo={dados.documentoPessoalRepresentante} 
                          tipoArquivo="documento representante"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className="common-fields">
              <div className="endereco-logradouro-grid">

                <div className="endereco-column">
                  <div className="form-group">
                    <label>Endereço da UC</label>
                    <input
                      type="text"
                      value={dados.enderecoUC || ''}
                      onChange={(e) => setDados({...dados, enderecoUC: e.target.value})}
                      placeholder="Endereço resumido da Unidade Consumidora"
                    />
                  </div>

                  <div className="form-row" style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginTop: '10px'}}>
                    <div className="form-group">
                      <label>CEP</label>
                      <input
                        type="text"
                        value={dados.CEP_UC || ''}
                        onChange={(e) => setDados({...dados, CEP_UC: e.target.value})}
                        placeholder="00000-000"
                        maxLength="9"
                      />
                    </div>
                    <div className="form-group">
                      <label>Bairro</label>
                      <input
                        type="text"
                        value={dados.Bairro_UC || ''}
                        onChange={(e) => setDados({...dados, Bairro_UC: e.target.value})}
                        placeholder="Nome do bairro"
                      />
                    </div>
                  </div>

                  <div className="form-row" style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px'}}>
                    <div className="form-group">
                      <label>Cidade</label>
                      <input
                        type="text"
                        value={dados.Cidade_UC || ''}
                        onChange={(e) => setDados({...dados, Cidade_UC: e.target.value})}
                        placeholder="Nome da cidade"
                      />
                    </div>
                    <div className="form-group">
                      <label>Estado</label>
                      <select
                        value={dados.Estado_UC || ''}
                        onChange={(e) => setDados({...dados, Estado_UC: e.target.value})}
                      >
                        <option value="">Selecione</option>
                        {ESTADOS_BRASIL.map(estado => (
                          <option key={estado.sigla} value={estado.sigla}>
                            {estado.sigla} - {estado.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="logradouro-column">
                    <div className="form-group logradouro-field">
                      <label>Logadouro da UC</label>
                      <textarea
                        value={dados.logradouroUC || ''}
                        onChange={(e) => setDados({...dados, logradouroUC: e.target.value})}
                        placeholder="Descrição detalhada do logradouro, idêntico ao que está na fatura da Equatorial"
                        rows="6"
                      ></textarea>
                    </div>
                  </div>
                  {dados.isArrendamento && (
                    <div className="common-fields">
                      <div className="form-row">
                        <div className="form-group file-group">
                          <label>Contrato de Locação/Arrendamento</label>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange('contratoLocacao', e.target.files[0])}
                          />
                          {dados.contratoLocacao ? (
                            <div className="arquivo-existente">
                              <span className="arquivo-info">Contrato de Locação</span>
                              <BotoesDocumento 
                                nomeArquivo={dados.contratoLocacao} 
                                tipoArquivo="contrato locação"
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={dados.isArrendamento || false}
                        onChange={(e) => setDados({...dados, isArrendamento: e.target.checked})}
                      />
                      <span className="checkmark"></span>
                      UC em Arrendamento
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Endereço do Representante</label>
                    <input
                      type="text"
                      value={dados.enderecoRepresentante || ''}
                      onChange={(e) => setDados({...dados, enderecoRepresentante: e.target.value})}
                      placeholder="Endereço completo do representante"
                    />
                  </div>
                </div>
              </div>
              
              {/* Termo de Adesão - COM ÍCONES LUCIDE */}
              <div className="form-row">
                <div className="form-group file-group">
                  <label>Termo de Adesão</label>
                  <div className="form-row">
                    {/* WhatsApp do Cliente */}
                    <div className="form-group">
                      <label>WhatsApp do cliente</label>
                      <input
                        type="tel"
                        value={dados.whatsappRepresentante || ''}
                        onChange={(e) => setDados({...dados, whatsappRepresentante: e.target.value})}
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    {/* Email do Cliente */}
                    <div className="form-group">
                      <label>Email do cliente</label>
                      <input
                        type="email"
                        value={dados.emailRepresentante || ''}
                        onChange={(e) => setDados({...dados, emailRepresentante: e.target.value})}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>
                  
                  {/* ✅ ADICIONAR O GerarTermoButton AQUI */}
                  <GerarTermoButton
                    proposta={{ id: dados.propostaId || item.propostaId }}
                    dados={dados}
                    statusDocumento={getStatusDocumentoUC(dados.numeroUC || dados.numero_uc)} // ✅ Status específico
                    setStatusDocumento={(status) => setStatusDocumentoUC(dados.numeroUC || dados.numero_uc, status)} // ✅ Setter específico
                    onClose={onClose}
                    onSalvarAntes={async (dadosParaSalvar) => {
                      await onSave(dadosParaSalvar);
                    }}
                  />

                  {dados.termoAdesao && (
                    <div className="arquivo-existente">
                      <span className="arquivo-info" title={typeof dados.termoAdesao === 'string' ? dados.termoAdesao : dados.termoAdesao.name}>
                        {typeof dados.termoAdesao === 'string' ?
                          `Termo: ${dados.termoAdesao.length > 30 ? dados.termoAdesao.substring(0, 30) + '...' : dados.termoAdesao}` :
                          `Arquivo: ${dados.termoAdesao.name.length > 30 ? dados.termoAdesao.name.substring(0, 30) + '...' : dados.termoAdesao.name}`}
                      </span>
                      {typeof dados.termoAdesao === 'string' && (
                        <BotoesDocumento
                          nomeArquivo={dados.termoAdesao}
                          tipoArquivo="termo de adesão"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ✅ DOCUMENTOS EXTRAS - Campos Dinâmicos */}
              <div className="form-row" style={{marginTop: '15px'}}>
                <div className="form-group" style={{width: '100%'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px'}}>
                    <label style={{margin: 0}}>Documentos Extras</label>
                    <button
                      type="button"
                      onClick={() => {
                        const novosExtras = [...(dados.documentosExtras || []), null];
                        setDados({...dados, documentosExtras: novosExtras});
                      }}
                      className="btn btn-primary"
                      title="Adicionar novo documento extra"
                      style={{
                        padding: '4px 10px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Plus size={14} />
                      Adicionar
                    </button>
                  </div>

                  {dados.documentosExtras && dados.documentosExtras.length > 0 && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                      {dados.documentosExtras.map((docExtra, index) => (
                        <div key={index} className="documento-extra-item">
                          <div className="documento-extra-header">
                            <span className="documento-extra-titulo">
                              Documento Extra #{index + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const novosExtras = dados.documentosExtras.filter((_, i) => i !== index);
                                setDados({...dados, documentosExtras: novosExtras});
                              }}
                              style={{
                                padding: '5px 10px',
                                fontSize: '12px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="Remover documento"
                            >
                              <Trash2 size={14} />
                              Remover
                            </button>
                          </div>

                          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                const novosExtras = [...(dados.documentosExtras || [])];
                                novosExtras[index] = file;
                                setDados({...dados, documentosExtras: novosExtras});
                              }}
                              style={{
                                padding: '8px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            />

                            {docExtra && (
                              <div className="arquivo-existente" style={{
                                margin: 0,
                                padding: '8px 12px',
                                backgroundColor: '#e8f5e9',
                                borderRadius: '4px',
                                border: '1px solid #c8e6c9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <span className="arquivo-info" style={{fontSize: '13px', color: '#2e7d32'}}>
                                  {typeof docExtra === 'string' ?
                                    (docExtra.length > 35 ? docExtra.substring(0, 35) + '...' : docExtra) :
                                    (docExtra.name.length > 35 ? docExtra.name.substring(0, 35) + '...' : docExtra.name)}
                                </span>
                                {typeof docExtra === 'string' && (
                                  <BotoesDocumento
                                    nomeArquivo={docExtra}
                                    tipoArquivo={`documento extra ${index + 1}`}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Automático - Apenas Visualização */}
          <div className="secao-modal">
            <h4 className="titulo-secao">
              <BarChart3 size={18} />
              Status da UC
            </h4>
            <div className="form-row">
              <div className="form-group">
                <div className="status-display">
                  <div className="status-atual">
                    <strong>Status atual:</strong> 
                    <span className={`status-badge status-${(dados.status || 'Aguardando').toLowerCase()}`}>
                      {dados.status || 'Aguardando'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className={`btn btn-primary ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
              onClick={(e) => {
                console.log('🔴 BOTÃO CLICADO - Salvar Alterações');
                console.log('🔍 Evento:', e);
                console.log('🔍 Loading atual no clique:', loading);
              }}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-inline"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Salvar Alterações
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProspecPage;